import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import Seccion from "../../Seccion";

const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const fmtN = (v) => new Intl.NumberFormat("es-MX").format(v || 0);
const fmtM = (v, moneda = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda === "MXN" ? "MXN" : "USD" }).format(v || 0);

function ComprasCreate() {
  const { user } = useContext(AuthContext);
  const URL_ORDENES = process.env.REACT_APP_URL_ORDENES_COMPRA;
  const URL_ARTICULOS_OC = process.env.REACT_APP_URL_ARTICULOS_OC;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_COMPRAS = process.env.REACT_APP_URL_COMPRAS;

  const history = useHistory();
  const ocInicial = new URLSearchParams(useLocation().search).get("oc") || "";

  const [ordenes, setOrdenes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedOC, setSelectedOC] = useState(ocInicial);
  const [renglones, setRenglones] = useState([]);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [factura, setFactura] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [tipoCambio, setTipoCambio] = useState(1);
  const [observaciones, setObservaciones] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [gastos, setGastos] = useState({ flete: "", aduana: "", ieps: "", otros: "" });

  useEffect(() => {
    axios.get(URL_ORDENES, { headers: headers() })
      .then((r) => setOrdenes(r.data.filter((o) => o.is_active === "Si" && o.status === "Abierto")))
      .catch(console.log);
    axios.get(URL_AREAS, { headers: headers() })
      .then((r) => setAreas(r.data.filter((a) => a.is_active === "Si")))
      .catch(console.log);
  }, [URL_ORDENES, URL_AREAS]);

  useEffect(() => {
    if (!selectedOC) { setRenglones([]); return; }
    axios.get(`${URL_ARTICULOS_OC}/ordenesCompra/${selectedOC}`, { headers: headers() })
      .then((r) => setRenglones(
        r.data
          .filter((a) => a.is_active === "Si" && a.pendiente_recibir > 0)
          .map((a) => ({
            id: a._id,
            vino: `${a.articulos[0].codigo} ${a.articulos[0].nombre}`,
            botellasPorCaja: a.articulos[0].piezasPorCaja,
            pedido: a.cantidad,
            recibido: a.recibido,
            pendiente: a.pendiente_recibir,
            precio: a.precio,
            cantidad: "",
            lote: "",
            caducidad: "",
          }))
      ))
      .catch(console.log);
  }, [selectedOC, URL_ARTICULOS_OC]);

  const orden = ordenes.find((o) => o._id === selectedOC);
  const moneda = orden?.moneda || "MXN";
  const esExtranjera = moneda !== "MXN";

  const totales = useMemo(() => renglones.reduce((t, r) => {
    const c = parseFloat(r.cantidad) || 0;
    return { botellas: t.botellas + c, importe: t.importe + c * r.precio };
  }, { botellas: 0, importe: 0 }), [renglones]);

  const cambiar = (id, campo, valor) => setRenglones((prev) => prev.map((r) => (r.id === id ? { ...r, [campo]: valor } : r)));
  const recibirTodo = () => setRenglones((prev) => prev.map((r) => ({ ...r, cantidad: r.pendiente })));

  const excedido = renglones.some((r) => (parseFloat(r.cantidad) || 0) > r.pendiente);
  const puedeGuardar = !guardando && selectedOC && selectedArea && factura.trim() && totales.botellas > 0
    && !excedido && (!esExtranjera || parseFloat(tipoCambio) > 0);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.post(`${URL_COMPRAS}/recepcionOC`, {
        ordenesCompra: selectedOC,
        fecha,
        factura: factura.trim(),
        areas: selectedArea,
        tipoCambio: esExtranjera ? parseFloat(tipoCambio) : 1,
        observaciones: observaciones.trim(),
        articulos: renglones
          .filter((r) => parseFloat(r.cantidad) > 0)
          .map((r) => ({ articulosOC: r.id, cantidad: parseFloat(r.cantidad), lote: r.lote, caducidad: r.caducidad })),
        gastos,
      }, { headers: headers() });
      await Swal.fire("Recepción guardada", "El inventario y la cuenta por pagar ya se actualizaron", "success");
      history.push("/ListadoCompras");
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se guardó", text: error.response?.data?.message || "Intenta de nuevo" });
      setGuardando(false);
    }
  };

  if (!user.compras_create) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-12">
        <div className="page-header">
          <div>
            <h3 className="page-title">Recepción de vino</h3>
            <p className="page-subtitle">Recibe total o parcialmente sobre una orden de compra</p>
          </div>
        </div>

        <form onSubmit={guardar}>
          <Row>
            <Col md={4}>
              <Label>Orden de compra</Label>
              <Input type="select" value={selectedOC} onChange={(e) => setSelectedOC(e.target.value)} required>
                <option value="">Selecciona una orden</option>
                {ordenes.map((o) => (
                  <option key={o._id} value={o._id}>
                    OC {o.idOrdenCompra} · {o.proveedores[0]?.nombre_comercial} · {fmtN(o.total_cantidad - o.recibido)} por recibir
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Fecha</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
            </Col>
            <Col md={2}>
              <Label>Factura del proveedor</Label>
              <Input value={factura} onChange={(e) => setFactura(e.target.value)} required />
            </Col>
            <Col md={2}>
              <Label>Almacén</Label>
              <Input type="select" value={selectedArea} onChange={(e) => setSelectedArea(e.target.value)} required>
                <option value="">Selecciona</option>
                {areas.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
              </Input>
            </Col>
            {esExtranjera && (
              <Col md={2}>
                <Label>Tipo de cambio {moneda}</Label>
                <Input type="number" min="0" step="0.0001" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} required />
              </Col>
            )}
          </Row>
          <Row className="mt-2">
            <Col md={12}>
              <Label>Observaciones</Label>
              <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
            </Col>
          </Row>

          {renglones.length > 0 && (
            <>
              <div className="table-toolbar mt-3">
                <span>{renglones.length} vinos pendientes</span>
                <Button type="button" size="sm" color="secondary" onClick={recibirTodo}>Recibir todo lo pendiente</Button>
              </div>
              <Table size="sm" responsive>
                <thead>
                  <tr>
                    <th>Vino</th><th className="text-right">Pedido</th><th className="text-right">Recibido</th>
                    <th className="text-right">Pendiente</th><th className="text-right">Costo</th>
                    <th style={{ width: 110 }}>Recibir</th><th style={{ width: 130 }}>Lote</th><th style={{ width: 150 }}>Caducidad</th><th className="text-right">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {renglones.map((r) => {
                    const c = parseFloat(r.cantidad) || 0;
                    return (
                      <tr key={r.id}>
                        <td>{r.vino}</td>
                        <td className="text-right">{fmtN(r.pedido)}</td>
                        <td className="text-right">{fmtN(r.recibido)}</td>
                        <td className="text-right">{fmtN(r.pendiente)}</td>
                        <td className="text-right td-money">{fmtM(r.precio, moneda)}</td>
                        <td>
                          <Input type="number" min="0" max={r.pendiente} bsSize="sm" value={r.cantidad}
                            invalid={c > r.pendiente} onChange={(e) => cambiar(r.id, "cantidad", e.target.value)} />
                        </td>
                        <td><Input bsSize="sm" placeholder="Automatico" value={r.lote} onChange={(e) => cambiar(r.id, "lote", e.target.value)} /></td>
                        <td><Input bsSize="sm" type="date" value={r.caducidad} onChange={(e) => cambiar(r.id, "caducidad", e.target.value)} /></td>
                        <td className="text-right td-money">{fmtM(c * r.precio, moneda)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="text-right negrita">Totales</td>
                    <td className="negrita" colSpan={3}>{fmtN(totales.botellas)} botellas</td>
                    <td className="text-right negrita td-money">{fmtM(totales.importe, moneda)}</td>
                  </tr>
                </tfoot>
              </Table>
              <Seccion titulo="Gastos para poner el vino en bodega" icono="fas fa-truck" ayuda="En pesos. Se reparten por valor en el costo real de cada vino y no van a la cuenta del proveedor.">
                <Row>
                  {[["flete", "Flete"], ["aduana", "Aduana"], ["ieps", "IEPS de importacion"], ["otros", "Otros"]].map(([k, t]) => (
                    <Col md={3} key={k}>
                      <Label>{t}</Label>
                      <Input type="number" min="0" step="any" value={gastos[k]} onChange={(e) => setGastos({ ...gastos, [k]: e.target.value })} />
                    </Col>
                  ))}
                </Row>
              </Seccion>
            </>
          )}

          <div className="mt-3">
            <Button color="primary" type="submit" disabled={!puedeGuardar}>
              {guardando ? "Guardando..." : "Guardar recepción"}
            </Button>{" "}
            <Button color="secondary" type="button" onClick={() => history.push("/ListadoPendRecibir")}>Regresar</Button>
          </div>
        </form>
      </div>
    </>
  );
}

export default ComprasCreate;
